import React, { useContext, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import PhonelinkRingOutlinedIcon from "@mui/icons-material/PhonelinkRingOutlined";

import { AlertContext } from "../../../context/AlertContext";
import { ServiciosContext } from "../../../context/ServiciosContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../theme/brand";
import Spinner from "../Spinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const OPCIONES_BUSQUEDA = [
  { title: "Apellido y nombre" },
  { title: "Domicilio" },
  { title: "Documento" },
  { title: "Teléfono" },
  { title: "Número de socio" },
  { title: "Plan" },
  { title: "Localidad" },
  { title: "Provincia" },
];

export default function Buscar() {
  const [labelTextBuscar, guardarLabelTextBuscar] = useState(
    OPCIONES_BUSQUEDA[0].title
  );
  const [valOpcionBusqueda, guardarValOpcionBusqueda] = useState(
    OPCIONES_BUSQUEDA[0]
  );

  const { mensajeAlerta } = useContext(AlertContext);

  const {
    antecedentes,
    tratamientos,
    obtenerTope,
    obtenerAdicionales,
    configuracion,
  } = useContext(ConfiguracionContext);

  const {
    valBusqueda,
    rows,
    selectedRow,
    guardarValBusqueda,
    capturarTelefonoPaciente,
    obtenerDatosPacientes,
    guardarAfiliado,
    buscarTitular,
    buscarValorizacion,
    setSelectedRow,
    obtenerCantidadServiciosTitular,
    guardarServiciosDni,
  } = useContext(ServiciosContext);

  const theme = createBrandTheme(getBrand(configuracion));
  const enMovil = useMediaQuery("(max-width:600px)");

  const nroSocio = (r) => String(r.id ?? "").replace(/^0+(?=\d)/, "");

  const enfocarBuscar = () => {
    const campo = document.getElementById("buscar");
    if (campo) campo.focus();
  };

  const onSelectAutocomplete = (e, value) => {
    if (value) {
      guardarLabelTextBuscar(value.title);
      guardarValOpcionBusqueda(value);
      guardarValBusqueda("");
      enfocarBuscar();
    }
  };

  const onClickCaptura = () => {
    guardarValOpcionBusqueda(OPCIONES_BUSQUEDA[3]); // Teléfono
    capturarTelefonoPaciente({ id: 308 });
    guardarLabelTextBuscar("Teléfono");
    enfocarBuscar();
  };

  const calcularEdad = (fecha) => {
    var hoy = new Date();
    var cumpleanos = new Date(fecha);
    var edad = hoy.getFullYear() - cumpleanos.getFullYear();
    var m = hoy.getMonth() - cumpleanos.getMonth();

    if (m < 0 || (m === 0 && hoy.getDate() < cumpleanos.getDate())) {
      edad--;
    }
    return edad;
  };

  // Estado del afiliado -> Chip. El TEXTO es el mismo que los dos habilitadoTemplate
  // previos (según habilitado/suspendido). El COLOR replica el classNames de cada uno:
  // para destinatario !== 1 el estado "Suspendido" sólo se pinta de amarillo si
  // fsuspendidohasta === "2030-01-01" (si no, va verde, como el original).
  const estadoTemplate = (rowData) => {
    const { habilitado, suspendido, fsuspendidohasta } = rowData;
    const label =
      habilitado && !suspendido
        ? "Habilitado"
        : habilitado && suspendido
        ? "Suspendido"
        : "Inhabilitado";

    let color;
    if (!habilitado) {
      color = "error";
    } else if (configuracion && configuracion.destinatario === 1) {
      color = suspendido ? "warning" : "success";
    } else {
      color =
        suspendido && fsuspendidohasta === "2030-01-01" ? "warning" : "success";
    }
    return <Chip size="small" label={label} color={color} variant="outlined" />;
  };

  const seleccionarAfiliado = (e) => {
    setSelectedRow(e.value);
    buscarTitular({ titularId: e.value.id_titular });
    buscarValorizacion({ titularId: e.value.id_titular });
    obtenerTope({ titularId: e.value.id_titular });
    obtenerAdicionales({ titularId: e.value.id_titular });
    obtenerCantidadServiciosTitular({ titularId: e.value.id_titular });
    guardarAfiliado({
      titularId: e.value.id_titular,
      id: e.value.id,
      nombre: e.value.nombre_razonsocial,
      telefono: e.value.telefono1,
      plan: e.value.planGrupo,
      dni: e.value.numeroDocumento,
      domicilio: e.value.domicilio,
      entreCalles: e.value.entreCalles,
      grupoId: e.value.id_grupo,
      planId: e.value.id_plan,
      coseguroDiurnoPediatrico: e.value.coseguroDiurnoPediatrico,
      coseguroNocturnoPediatrico: e.value.coseguroNocturnoPediatrico,
      coseguroDiurnoAdulto: e.value.coseguroDiurnoAdulto,
      coseguroNocturnoAdulto: e.value.coseguroNocturnoAdulto,
      provincia: "Buenos Aires",
      ...(e.value.nacimiento
        ? { nacimiento: e.value.nacimiento }
        : { nacimiento: null }),
      ...(e.value.nacimiento
        ? { edad: calcularEdad(e.value.nacimiento) }
        : { edad: null }),
      ...(e.value.sexo
        ? { sexo: e.value.sexo === "M" ? 1 : 2 }
        : { sexo: null }),
      ...(e.value.antecedenteId
        ? {
            antecedenteId: e.value.antecedenteId,
            antecedenteNombre: antecedentes.find(
              (item) => item.id === e.value.antecedenteId
            ).nombre,
          }
        : { antecedenteId: null, antecedenteNombre: null }),
      ...(e.value.tratamientoId
        ? {
            tratamientoId: e.value.tratamientoId,
            tratamientoNombre: tratamientos.find(
              (item) => item.id === e.value.tratamientoId
            ).nombre,
          }
        : { tratamientoId: null, tratamientoNombre: null }),
      ...(e.value.obraSocial
        ? {
            obraSocial: e.value.obraSocial,
            nroObraSocial: e.value.nroObraSocial,
          }
        : { obraSocial: null, nroObraSocial: null }),
      ...(e.value.celular
        ? { celular: String(e.value.celular).replace(/\D/g, "") }
        : { celular: null }),
      ...(e.value.email ? { email: e.value.email } : { email: null }),
    });
    guardarServiciosDni([]);
  };

  const onBuscarKeyPress = (ev) => {
    if (ev.key !== "Enter") return;
    ev.preventDefault();
    if (labelTextBuscar === "") {
      mensajeAlerta(
        "Toma de Servicio",
        "Debe seleccionar una opcion de busqueda !.",
        "error"
      );
      return;
    }
    if (valBusqueda === "") {
      mensajeAlerta(
        "Toma de Servicio",
        "Debe seleccionar un texto de busqueda !.",
        "error"
      );
      return;
    }
    setSelectedRow(null);
    obtenerDatosPacientes({
      opcion: labelTextBuscar,
      texto: valBusqueda,
      destinatario: configuracion.destinatario,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ pt: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <Autocomplete
              id="combo-box-busqueda"
              value={valOpcionBusqueda}
              options={OPCIONES_BUSQUEDA}
              getOptionLabel={(option) => option.title}
              noOptionsText="No hay opciones"
              size="small"
              isOptionEqualToValue={(option, value) =>
                option.title === value.title
              }
              onChange={(event, newValue) =>
                onSelectAutocomplete(event, newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Opciones de búsqueda"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={7} md={5}>
            <TextField
              id="buscar"
              name="buscar"
              value={valBusqueda}
              label={labelTextBuscar === "" ? "Texto de búsqueda" : labelTextBuscar}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              autoComplete="off"
              onChange={(e) => guardarValBusqueda(e.target.value)}
              onKeyPress={onBuscarKeyPress}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhonelinkRingOutlinedIcon />}
              onClick={onClickCaptura}
            >
              Capturar teléfono
            </Button>
          </Grid>
        </Grid>

        {!rows ? (
          <Box sx={{ mt: 2 }}>
            <Spinner />
          </Box>
        ) : enMovil ? (
          // En teléfono: lista de tarjetas en vez de tabla (sin scroll lateral).
          <Stack spacing={1} sx={{ mt: 2 }}>
            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No hay datos.
              </Typography>
            ) : null}
            {rows.map((r) => {
              const sel = selectedRow && selectedRow.id === r.id;
              return (
                <Paper
                  key={r.id}
                  elevation={0}
                  component={ButtonBase}
                  onClick={() => seleccionarAfiliado({ value: r })}
                  sx={{
                    width: "100%",
                    display: "block",
                    textAlign: "left",
                    p: 1.5,
                    border: "1px solid",
                    borderColor: sel ? "primary.main" : "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                      {r.nombre_razonsocial}
                    </Typography>
                    {estadoTemplate(r)}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Nº {nroSocio(r)} · Doc {r.numeroDocumento || "—"}
                  </Typography>
                  <Typography variant="body2">
                    Tel: {r.telefono1 || "—"}
                  </Typography>
                  {r.planGrupo ? (
                    <Typography variant="body2" color="text.secondary">
                      {r.planGrupo}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    {r.domicilio}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              // sin scroll horizontal: las celdas largas parten de línea
              "& .p-datatable-wrapper": { overflowX: "hidden" },
              "& .p-datatable table": { tableLayout: "fixed", width: "100%" },
              "& .p-datatable-thead > tr > th, & .p-datatable-tbody > tr > td": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                verticalAlign: "top",
              },
              "& .p-datatable-tbody > tr > td": { fontSize: "0.78rem" },
              "& .p-datatable-thead > tr > th": { fontSize: "0.75rem" },
            }}
          >
            <DataTable
              value={rows}
              selection={selectedRow}
              onSelectionChange={seleccionarAfiliado}
              selectionMode="single"
              dataKey="id"
              className="p-datatable-sm"
              rowHover
              emptyMessage="No hay datos."
              paginator
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
              rows={50}
              rowsPerPageOptions={[10, 20, 50, 100]}
            >
              <Column
                field="id_titular"
                header="Nº Socio"
                body={nroSocio}
                style={{ width: "9%" }}
                sortable
              />
              <Column
                field="nombre_razonsocial"
                header="Nombre / Razón Social"
                style={{ width: "22%" }}
                sortable
              />
              <Column
                field="numeroDocumento"
                header="Documento / CUIT"
                style={{ width: "12%" }}
                sortable
              />
              <Column
                field="telefono1"
                header="Teléfono"
                style={{ width: "12%" }}
                sortable
              />
              <Column field="planGrupo" header="Plan" style={{ width: "12%" }} />
              <Column
                field="domicilio"
                header="Domicilio"
                style={{ width: "24%" }}
                sortable
              />
              <Column
                field="habilitado"
                header="Estado"
                body={estadoTemplate}
                style={{ width: "9%" }}
                sortable
              />
            </DataTable>
          </Paper>
        )}
      </Box>
    </ThemeProvider>
  );
}
